import { Avatar, Box, Button, Container, CssBaseline, Divider, IconButton, Link, List, ListItemIcon, Menu, MenuItem, Toolbar, Tooltip, Typography, useMediaQuery } from "@mui/material"
import { useEffect, useState, useRef } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom"
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import { styled, useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import ListItems from "../dashboard-home/ListItems";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import MuiDrawer from '@mui/material/Drawer';
import { Logout } from "@mui/icons-material";
import { useAppSelector } from "../../../../hooks/store-hooks";
import { useAuthService } from "../../../../../use-cases/auth";
import { useUIService } from "../../../../../use-cases/ui";
import { useNavigationService } from "../../../../../use-cases/navigation";
import { SessionTimer } from "../../../../components/session-timer/SessionTimer";

function Copyright(props: any) {
  return (
    <Typography variant="body2" color="text.secondary" align="center" {...props}>
      {'Copyright © '}
      <Link color="inherit" href="https://veles.com.br/">
        Veles
      </Link>{' '}
      {new Date().getFullYear()}
      {'.'}
    </Typography>
  );
}
const drawerWidth: number = 240;
interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));


const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    '& .MuiDrawer-paper': {
      position: 'relative',
      whiteSpace: 'nowrap',
      width: drawerWidth,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      boxSizing: 'border-box',
      ...(!open && {
        overflowX: 'hidden',
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        width: theme.spacing(7),
        [theme.breakpoints.up('sm')]: {
          width: theme.spacing(9),
        },
      }),
    },
  }),
);


export const DashboardLayout = () => {
  const theme = useTheme()
  // State
  const { currentUser } = useAppSelector((state) => state.user)
  // Use State
  const isSm = useMediaQuery(theme.breakpoints.down('lg'))
  const [open, setOpen] = useState(isSm ? false : true);
  const [drawerVariant, setDrawerVariant] = useState<'permanent' | 'temporary'>('permanent')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const hasSetupSession = useRef(false);
  // Hook
  const location = useLocation()
  const navigate = useNavigate()
  const { changeTitleAndMetaDesc } = useUIService()
  const { logout, setupSession } = useAuthService()
  const { navigateTo } = useNavigationService()
  // Use Effect
  useEffect(() => {
    if (isSm) {
      if (open) {
        setDrawerVariant('permanent')

      } else {
        setDrawerVariant('temporary')
      }
    } else {
      setDrawerVariant('permanent')
    }
  }, [isSm, open])

  useEffect(() => {
    if (isSm) {
      setOpen(false)
    }
  }, [isSm, location])

  useEffect(() => {
    changeTitleAndMetaDesc({
      title: 'Veles- Dashboard',
      metaDesc: 'Acesse o painel administrativo'
    })
  }, [changeTitleAndMetaDesc])

  useEffect(() => {

    if (!hasSetupSession.current) {
      hasSetupSession.current = true;
      setupSession();
    }
  }, [setupSession])

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const openMenuAvatar = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <AppBar position="absolute" open={open}>
          <Toolbar
            sx={{
              pr: '24px',
            }}
          >
            <IconButton
              edge="start"
              color="inherit"
              aria-label="open drawer"
              onClick={toggleDrawer}
              sx={{
                marginRight: isSm ? 0 : '36px',
                ...(open && { display: 'none' }),
              }}
            >
              <MenuIcon />
            </IconButton>
            <div
              style={{
                flexGrow: 1,
                padding: `${theme.spacing(1)} 0`,
              }}
            >
              <Button
                onClick={() => { navigate('/dashboard') }}
                style={{
                  display: 'flex',
                  cursor: 'pointer',
                  padding: theme.spacing(1),
                  alignItems: 'center',
                  background: 'transparent',
                }}
              >
                <div
                  style={{
                    borderRadius: '50%',
                    background: theme.palette.primary.contrastText,
                    padding: '5px',
                    marginRight: '10px'
                  }}
                >
                  <img
                    src="/logo_dash_veles.png"
                    alt="Logo Veles"
                    style={{
                      width: '40px',
                      height: 'auto'
                    }}
                  />
                </div>
                <Typography
                  variant="h6"
                  fontWeight={"bold"}
                  color={theme.palette.primary.contrastText}
                  noWrap
                >
                  Dash Veles
                </Typography>
              </Button>
            </div>
            <SessionTimer />
            <Tooltip title="Account settings">
              <IconButton
                onClick={handleClick}
                size="small"
                sx={{ ml: 2 }}
                aria-controls={openMenuAvatar ? 'account-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={openMenuAvatar ? 'true' : undefined}
              >
                <Avatar sx={{ width: 32, height: 32 }}>
                  <Typography color="primary" fontWeight={"bold"}>
                    {
                      currentUser?.Nome[0]
                    }
                  </Typography>
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              id="account-menu"
              open={openMenuAvatar}
              onClose={handleClose}
              onClick={handleClose}
              slotProps={{
                paper: {
                  elevation: 0,
                  sx: {
                    overflow: 'visible',
                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                    mt: 1.5,
                    '& .MuiAvatar-root': {
                      width: 32,
                      height: 32,
                      ml: -0.5,
                      mr: 1,
                    },
                    '&::before': {
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      top: 0,
                      right: 14,
                      width: 10,
                      height: 10,
                      bgcolor: 'background.paper',
                      transform: 'translateY(-50%) rotate(45deg)',
                      zIndex: 0,
                    },
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >

              <MenuItem disabled sx={{ opacity: '0.95 !important', color: 'text.primary', fontWeight: 600, minWidth: 160 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  { currentUser?.Nome || "Usuário"}
                </Typography>
              </MenuItem>
              <Divider sx={{ my: 0.5 }} />

              <MenuItem onClick={() => {
                handleClose()
                logout()
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Logout fontSize="small" sx={{ color: 'error.main' }} />
                  <Typography textAlign="center" color="error.main" sx={{ fontWeight: 500 }}>
                    Sair
                  </Typography>
                </Box>
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <Drawer variant={drawerVariant} open={open}>

          <Toolbar
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              px: [1],
            }}
          >
            <Button fullWidth onClick={() => {
              const loginUrl = String(process.env.REACT_APP_VELES_APP_URL_LOGIN)
              const appBaseUrl = loginUrl.replace('/auth/login', '').replace('/authentication/login', '')
              window.localStorage.removeItem('LOGIN_TIMESTAMP')
              window.location.href = appBaseUrl + "/modulos"
            }}>
              <img src="/logo_veles.png" alt="Logo Veles" style={{ width: '100px' }} />
            </Button>
            <IconButton onClick={toggleDrawer}>
              <ChevronLeftIcon />
            </IconButton>
          </Toolbar>
          <Divider />
          <List component="nav">
            <ListItems />
          </List>
        </Drawer>
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode === 'light'
                ? theme.palette.grey[100]
                : theme.palette.grey[900],
            flexGrow: 1,
            height: '100vh',
            overflow: 'auto',
          }}
        >
          <Toolbar />
          <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Outlet />
            <Copyright sx={{ pt: 4 }} />

          </Container>
        </Box>
      </Box>
    </>
  )
}